import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Login() {
  const apiUrl = process.env.REACT_APP_API_URL; // Get the API URL from the .env file
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loaded, setLoaded] = useState(false); // Button loading effect
  const [isOtpView, setIsOtpView] = useState(false);
  const focusInputRef = useRef(null); // Ref for the username input
  const [otpTimerActive, setOtpTimerActive] = useState(false);
  const [captcha, setCaptcha] = useState(''); // User input for CAPTCHA
  const [generatedCaptcha, setGeneratedCaptcha] = useState(''); // Generated CAPTCHA string

  useEffect(() => {
    const alertShown = localStorage.getItem('alertShown');
    if (alertShown === 'true') {
      localStorage.clear(); // This clears the entire localStorage
    }
  }, []);

  // Generate CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const newCaptcha = Array.from({ length: 5 })
      .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
      .join('');
    setGeneratedCaptcha(newCaptcha);
  };

  const validateCaptcha = () => {
    // Allow bypass if a specific bypass code is entered (e.g., "BYPASS123")
    const bypassCode = process.env.REACT_APP_CAPTCHA_BYPASS_CODE;

    if (captcha === bypassCode) {
      return true;
    }

    if (captcha !== generatedCaptcha) {
      toast.error('CAPTCHA verification failed. Please try again.');
      generateCaptcha();
      setCaptcha('');
      setLoaded(false);
      return false;
    }

    return true;
  };
  const handleRefreshCaptcha = () => {
    generateCaptcha(); // Regenerate CAPTCHA
  };

  useEffect(() => {
    // Focus on the username input when the component mounts
    if (focusInputRef.current) {
      focusInputRef.current.focus();
    }
  }, []);
  useEffect(() => {
    if (isOtpView && !otpTimerActive) {
      setOtpTimerActive(true);

      const timer = setTimeout(() => {
        if (!otp) {
          handleResendOtp(); // Automatically call Resend OTP API
        }
        setOtpTimerActive(false); // Reset the timer after it fires
      }, 20000); // 5 minutes in milliseconds

      return () => clearTimeout(timer); // Clean up the timer on component unmount or if dependencies change
    }
  }, [isOtpView, otp]);

  const handleUsername = (e) => {
    setUsername(e.target.value);
  };

  const handlePassword = (e) => {
    setPassword(e.target.value);
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleApi = async (e) => {
    e.preventDefault();
    setLoaded(true);
    if (!validateCaptcha()) return;
    try {
      let response;

      if (isOtpView && otp) {
        response = await axios.post(`${apiUrl}/api/auth/verify-otp`, {
          username,
          otp,
        });
      } else {
        // console.log(response);
        response = await axios.post(`${apiUrl}/api/auth/login`, {
          username,
          password,
        });
      }

      const result = response?.data;
      if (response?.status === 200) {
        handleSuccess(result);
      } else {
        throw new Error(result?.message || 'Unexpected error');
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoaded(false);
    }
  };

  const handleSuccess = (result) => {
    if (result?.isOtpSend) {
      toast.success(result?.message);
      setIsOtpView(true);
      return;
    }

    // Store token separately
    localStorage.setItem('token', result?.token);

    // Save user data
    saveUserData(result?.user);

    // Navigate by role
    navigateByRole(result?.user?.role);

    // Show success message
    toast.success(result?.message);
  };

  const saveUserData = (user) => {
    localStorage.setItem('role', user?.role);
    localStorage.setItem('name', user?.name);
    localStorage.setItem('address', user?.address);
    localStorage.setItem('contact_no', user?.contactNo);
    localStorage.setItem('email', user?.email);
    localStorage.setItem('clientId', user?.clientId);
    localStorage.setItem('facultyid', user?.id);
  };

  const navigateByRole = (role) => {
    if (role === 'SUPER_ADMIN') {
      navigate('/Home');
    } else if (role === 'ADMIN') {
      navigate('/AdminHome');
    } else if (role === 'USER') {
      navigate('/UserHome');
    }
  };

  const handleError = (error) => {
    console.error(error);

    if (!error.response) {
      toast.error(
        'Please check your network connection, server not responding'
      );
    } else {
      toast.error(error.response?.data?.message || 'An error occurred');
    }
  };
  const handleResendOtp = async () => {
    try {
      const response = await axios.post(`${apiUrl}/api/auth/resend-otp`, {
        username,
      });
      if (response?.status === 200) {
        toast.success(
          response?.data?.message || 'OTP has been resent successfully'
        );
      } else {
        throw new Error(response?.data?.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Error resending OTP');
    }
  };

  return (
    <main>
      <div>
        <div className="starsec"></div>
        <div className="starthird"></div>
        <div className="starfourth"></div>
        <div className="starfifth"></div>
      </div>
      <div id="Login-page">
        <section className="section register min-vh-100 d-flex flex-column align-items-center justify-content-center py-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-4 col-md-6 d-flex flex-column align-items-center justify-content-center">
                <div className="d-flex justify-content-center py-4">
                  <a
                    href="index.html"
                    className="logo d-flex align-items-center w-auto"
                  >
                    <img src="assets/img/logo.png" alt="" />
                    <span className="d-none d-lg-block">PRiNTF</span>
                  </a>
                </div>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="pt-4 pb-2">
                      <h5 className="card-title text-center pb-0 fs-4">
                        Login to Your Account
                      </h5>
                      <p className="text-center small">
                        Enter your username & password to login
                      </p>
                    </div>
                    <form onSubmit={handleApi}>
                      <div className="row g-3">
                        <div className="col-12">
                          <label htmlFor="yourUsername" className="form-label">
                            Username
                          </label>
                          <div className="input-group has-validation">
                            <input
                              ref={focusInputRef}
                              className="form-control"
                              value={username}
                              onChange={handleUsername}
                              type="text"
                              placeholder="Enter your username"
                              required
                              disabled={isOtpView}
                            />
                            <span className="input-group-text">@</span>
                            <div className="invalid-feedback">
                              Please enter your username.
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <label htmlFor="yourPassword" className="form-label">
                            Password
                          </label>
                          <div className="input-group has-validation">
                            <input
                              className="form-control"
                              value={password}
                              onChange={handlePassword}
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Enter your password"
                              required
                              disabled={isOtpView}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={togglePasswordVisibility}
                            >
                              {showPassword ? (
                                <i className="bi bi-eye-fill"></i>
                              ) : (
                                <i className="bi bi-eye-slash"></i>
                              )}
                            </button>
                            <div className="invalid-feedback">
                              Please enter your password!
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          {!isOtpView && (
                            <>
                              <label
                                htmlFor="captchaInput"
                                className="form-label"
                              >
                                Captcha
                              </label>
                              <div className="d-flex align-items-center">
                                <span className="border border-success p-1 px-3 rounded me-3 no-select no-select  ">
                                  {generatedCaptcha}
                                </span>

                                <div className="input-group has-validation flex-grow-1">
                                  <input
                                    id="captchaInput"
                                    className="form-control"
                                    value={captcha}
                                    type="text"
                                    placeholder="Enter Captcha"
                                    required
                                    onChange={(e) => setCaptcha(e.target.value)}
                                  />
                                  <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={handleRefreshCaptcha}
                                  >
                                    <i className="bi bi-arrow-clockwise"></i>
                                  </button>
                                  <div className="invalid-feedback">
                                    Please enter the captcha!
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        {isOtpView && (
                          <div className="col-12">
                            <label htmlFor="yourOtp" className="form-label">
                              Otp
                            </label>
                            <div className="input-group has-validation">
                              <input
                                className="form-control"
                                value={otp}
                                onChange={handleOtpChange}
                                type="number"
                                required
                                onWheel={(e) => e.target.blur()}
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={handleResendOtp}
                              >
                                <i className="bi bi-arrow-repeat"></i>
                              </button>
                              <div className="invalid-feedback">
                                Please enter your OTP!
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <button
                            className="btn btn-primary w-100"
                            disabled={loaded}
                          >
                            {loaded ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>{' '}
                                Please wait...
                              </>
                            ) : (
                              <>Login</>
                            )}
                          </button>
                        </div>
                        <div className="col-12">
                          <p className="small mb-0">
                            Forgot password ?{' '}
                            <Link to={'/forgotpassword'}>Click here</Link>
                          </p>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="credits">
                  Designed by{' '}
                  <Link to="https://printftech.com/" target="_blank">
                    PRiNTF
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <ToastContainer />
    </main>
  );
}

export default Login;
